import { useMount } from '@contra/react-hooks/useMount';
import {
  type KeyboardEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

type RegisterElement = <Element extends TextElement = TextElement>(
  element: Element | null,
) => void;

type TextElement = HTMLInputElement | HTMLTextAreaElement;

type UseIsTypingProps = {
  /**
   * Time in milliseconds before typing indicator gets reset. **Default:** `1000`
   */
  timeout?: number;
};

export const useIsTyping = ({ timeout = 1_000 }: UseIsTypingProps = {}): [
  boolean,
  RegisterElement,
] => {
  const [isTyping, setIsTyping] = useState(false);
  const [currentElement, setCurrentElement] = useState<null | TextElement>(
    null,
  );
  const timeoutRef = useRef<null | ReturnType<typeof setTimeout>>(null);

  const reset = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      setIsTyping(false);
    }, timeout);
  }, [timeout]);

  const register: RegisterElement = useCallback((element) => {
    setCurrentElement(element);
    if (!element) {
      setIsTyping(false);
    }
  }, []);

  useMount(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  });

  useEffect(() => {
    setIsTyping(false);
    if (!currentElement) {
      return;
    }

    const keyUpDownListener = (event: Event) => {
      const value = (event.target as TextElement).value;
      const isMetaKey = (() => {
        if (event instanceof KeyboardEvent) {
          return event.metaKey || event.ctrlKey || event.altKey;
        }

        return false;
      })();

      const hasValue = Boolean(value && value.length > 0);

      setIsTyping(Boolean(hasValue && !isMetaKey));
      reset();
    };

    const blurListener = () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      setIsTyping(false);
    };

    currentElement.addEventListener('keyup', keyUpDownListener);
    currentElement.addEventListener('keydown', keyUpDownListener);
    currentElement.addEventListener('blur', blurListener);

    // eslint-disable-next-line consistent-return
    return () => {
      currentElement.removeEventListener('keydown', keyUpDownListener);
      currentElement.removeEventListener('keyup', keyUpDownListener);
      currentElement.removeEventListener('blur', blurListener);
    };
  }, [currentElement, reset]);

  return [isTyping, register];
};
